import axios from 'axios';
import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
  JobData,
  JobListResponse,
  JobRequest,
  JobResponse,
  JOB_TYPE,
} from '../../types/dataService/Job';
import URL_CONST from '../../config/url';
import {VideoRegionProps} from '../../types/dataset/DatasetBinary';
import {getHeaders} from './index';

const JOB_TIMEOUT = 3000;
export type JobMap = {[key: string]: JobData};

export type DatasetJobState = {
  inProgress: string[]; // JobIDs that are in progress
  inProgressRequest: string[]; // binaryIDs that are in progress
  isBusy: boolean; //convenience flag to check if there are Jobs in progress
  jobs: JobMap;
};

export const DATASET_JOB = 'datasetJob';

export const runJob = createAsyncThunk(
  `${DATASET_JOB}/run`,
  async (args: {
    datasetId: string;
    type: JOB_TYPE;
    version?: number;
    timeout?: number;
    // ALTER CATEGORIES param
    delete?: number[];
    sourceDatasetId?: string;
    // VIDEO SAMPLING param
    binaryId?: string;
    regions?: VideoRegionProps[];
    // Dataset Assingment
    projectId?: string | null;
    sceneDetectorThreshold?: number;
  }) => {
    const {
      type,
      timeout,
      sourceDatasetId,
      regions,
      binaryId,
      sceneDetectorThreshold,
      ...rest
    } = args;
    const {data} = await axios.put<JobResponse>(
      URL_CONST.DSS_DATASET_JOB,
      {type, sourceDatasetId, binaryId, regions, sceneDetectorThreshold},
      {
        params: {
          ...rest,
        },
        headers: await getHeaders({
          'Request-Timeout': timeout || JOB_TIMEOUT,
        }),
      }
    );
    return data;
  }
);

export const cancel = createAsyncThunk(
  `${DATASET_JOB}/cancel`,
  async (args: {datasetId: string; jobId: string}) => {
    const {data} = await axios.post<JobResponse>(
      `${URL_CONST.DSS_DATASET_JOB}/cancel`,
      null,
      {
        headers: await getHeaders(),
        params: {
          ...args,
        },
      }
    );
    return data;
  }
);

export const finalize = createAsyncThunk(
  `${DATASET_JOB}/finalize`,
  async (args: {datasetId: string; jobId: string}) => {
    const {data} = await axios.post<JobResponse>(
      `${URL_CONST.DSS_DATASET_JOB}/finalize`,
      null,
      {
        headers: await getHeaders(),
        params: {
          ...args,
        },
      }
    );
    return data;
  }
);

export const getList = createAsyncThunk(
  `${DATASET_JOB}/list`,
  async (datasetId: string) => {
    const {data} = await axios.get<JobListResponse>(
      URL_CONST.DSS_DATASET_JOBS,

      {
        headers: await getHeaders(),
        params: {
          datasetId,
        },
      }
    );
    return data.body;
  }
);

const initialState: DatasetJobState = {
  inProgress: [],
  inProgressRequest: [],
  isBusy: false,
  jobs: {},
};

const jobSlice = createSlice({
  name: DATASET_JOB,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
    get(state, action: PayloadAction<JobRequest>) {
      // Check the JobSagas.js for polling api
      const {jobId} = action.payload;
      !state.inProgress.includes(jobId) && state.inProgress.push(jobId);
      state.isBusy = true;
    },
    update(state, action: PayloadAction<JobResponse>) {
      const {payload: data} = action;
      state.jobs = {
        ...state.jobs,
        [data.body.id]: data.body,
      };
      // In case of VIDEO SAMPLING track the binary/video that is in progress
      // This may be used on other job types
      if (data && data.body.request) {
        const reqId = data.body.request.binaryId;
        if (data.body.finished) {
          state.inProgressRequest = state.inProgressRequest.filter(el => el !== reqId);
        } else if (!state.inProgressRequest.includes(reqId)) {
          state.inProgressRequest = [...state.inProgressRequest, reqId];
        }
      }
    },
    cancel(state, action: PayloadAction<{jobId: string}>) {
      // Remove job tracking
      const {jobId} = action.payload;
      const inProgress = state.inProgress.filter(id => id !== jobId);
      state.inProgress = inProgress;
      state.isBusy = !!inProgress.length;
    },
    remove(state, action: PayloadAction<{jobId: string}>) {
      // Remove job status data
      const {jobId} = action.payload;
      const inProgress = state.inProgress.filter(id => id !== jobId);
      state.inProgress = inProgress;
      state.isBusy = !!inProgress.length;
      delete state.jobs[jobId];
    },
  },
  extraReducers: builder => {
    builder.addCase(getList.fulfilled, (state, action) => {
      const jobs = action.payload;
      const jobMap: JobMap = {};
      jobs.forEach(el => (jobMap[el.id] = el));

      state.jobs = {
        ...state.jobs,
        ...jobMap,
      };
    });
    // TODO: Implement error handling for jobs
    // .addCase(runJob.rejected, (state, action) => {
    //   console.log('!!action: ', action);
    //   // state.isBusy = false;
    // });
  },
});

export const {actions: datasetJobActions, reducer: datasetJobReducer} = jobSlice;
